import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context

const Reading = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'reading'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container reading-page">
      <h1 className="page-title">Reading and Intellectual Growth</h1>

      {/* Include the image */}
      <img
        src="/images/wf-reading.webp"
        alt="Reading"
        className="page-image"
      />

      <p className="page-intro">
        Welcome to the Reading section of Wholeness Forward. Reading is a fundamental tool for personal growth, intellectual development, and mental well-being.
      </p>

      <p className="page-text">
        Regular reading has far-reaching benefits that extend beyond intellectual stimulation. It fosters empathy, reduces stress, improves focus, and enhances creativity.
      </p>

      <hr className="page-divider" />

      <h2 className="section-title">Health Benefits of Reading</h2>
      <ul className="content-list">
        <li><strong>Reduced Stress:</strong> Immersing yourself in a good book can help lower stress levels.</li>
        <li><strong>Improved Cognitive Function:</strong> Reading stimulates the brain, enhancing memory and problem-solving skills.</li>
        <li><strong>Better Sleep:</strong> A bedtime reading routine can signal your brain to wind down.</li>
        <li><strong>Enhanced Empathy:</strong> Reading diverse perspectives fosters emotional intelligence.</li>
        <li><strong>Mental Longevity:</strong> Regular reading reduces the risk of cognitive decline.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="section-title">Curated Video Collection</h2>
      <p className="section-description">Explore these videos to learn more about how reading can transform your life:</p>

      {loading ? (
        <p className="loading-message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}

      <hr className="page-divider" />

      <h3 className="subheading">Community Reading Discussion</h3>
      <p className="page-text">
        Connect with fellow book enthusiasts in our community. Share your favorite reads, discover new recommendations, and discuss how reading has impacted your personal and intellectual growth.
      </p>
      <a href="/forum/Reading" className="btn">Join the Discussion</a>
    </div>
  );
};

export default Reading;
